import { css, cx } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import type { IconProps, IconSize } from "@octopusdeploy/design-system-icons";
import { CheckIcon, CheckWarningCircleIcon, ClockRotateLeftIcon, DashedCircleIcon, SpinnerIcon, TimeCircleIcon, UserClockIcon, UserMagnifyingGlassIcon, UserXmarkIcon, XmarkIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
export const taskStatuses = ["queued", "executing", "success", "success-with-warnings", "failed", "canceling", "canceled", "timed-out", "pending-intervention", "no-data"] as const;
export type TaskStatusType = (typeof taskStatuses)[number];
export type TaskStatusSize = 28 | 32 | 36;
export interface TaskStatusProps {
    /**
     * The status of the task.
     */
    status: TaskStatusType;
    /**
     * The size of the status circle.
     */
    size: TaskStatusSize;
    /**
     * Optional tooltip text to display on hover.
     */
    tooltip?: string;
}
export function TaskStatus({ status, size, tooltip }: TaskStatusProps) {
    const { icon: StatusIcon, backgroundStyles, iconColor, label } = getStatusDetails(status);
    const icon = <StatusIcon size={iconSizeMap[size]} color={iconColor} title={label}/>;
    const statusCircle = <div className={cx(styles.root, styles.sizes[size], backgroundStyles)}>{status === "executing" ? <span className={styles.spinning}>{icon}</span> : icon}</div>;
    return tooltip ? <Tooltip content={tooltip}>{statusCircle}</Tooltip> : statusCircle;
}
const iconSizeMap: Record<TaskStatusSize, IconSize> = {
    28: 16,
    32: 20,
    36: 24,
};
interface TaskStatusDetails {
    icon: React.ComponentType<IconProps>;
    backgroundStyles: string;
    iconColor: string;
    label: string;
}
function getStatusDetails(status: TaskStatusType): TaskStatusDetails {
    switch (status) {
        case "queued":
            return {
                icon: TimeCircleIcon,
                backgroundStyles: styles.status.neutral,
                iconColor: themeTokens.color.taskStatus.icon.inverse,
                label: "Queued",
            };
        case "executing":
            return {
                icon: SpinnerIcon,
                backgroundStyles: styles.status.executing,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Executing",
            };
        case "failed":
            return {
                icon: XmarkIcon,
                backgroundStyles: styles.status.failure,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Failed",
            };
        case "canceling":
            return {
                icon: UserClockIcon,
                backgroundStyles: styles.status.warning,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Canceling",
            };
        case "canceled":
            return {
                icon: UserXmarkIcon,
                backgroundStyles: styles.status.warning,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Canceled",
            };
        case "timed-out":
            return {
                icon: ClockRotateLeftIcon,
                backgroundStyles: styles.status.warning,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Timed out",
            };
        case "success":
            return {
                icon: CheckIcon,
                backgroundStyles: styles.status.success,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Success",
            };
        case "pending-intervention":
            return {
                icon: UserMagnifyingGlassIcon,
                backgroundStyles: styles.status.neutral,
                iconColor: themeTokens.color.taskStatus.icon.inverse,
                label: "Pending intervention",
            };
        case "success-with-warnings":
            return {
                icon: CheckWarningCircleIcon,
                backgroundStyles: styles.status.success,
                iconColor: themeTokens.color.taskStatus.icon.default,
                label: "Success with warnings",
            };
        case "no-data":
            return {
                icon: DashedCircleIcon,
                backgroundStyles: styles.status.neutral,
                iconColor: themeTokens.color.taskStatus.icon.inverse,
                label: "No data",
            };
        default:
            return exhaustiveCheck(status, `Unexpected status ${status} for TaskStatusType`);
    }
}
const styles = {
    root: css({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: borderRadius.circle,
    }),
    status: {
        success: css({ backgroundColor: themeTokens.color.taskStatus.background.success }),
        executing: css({ backgroundColor: themeTokens.color.taskStatus.background.executing }),
        warning: css({ backgroundColor: themeTokens.color.taskStatus.background.warning }),
        failure: css({ backgroundColor: themeTokens.color.taskStatus.background.failure }),
        neutral: css({ backgroundColor: themeTokens.color.taskStatus.background.neutral }),
    },
    sizes: {
        28: css({
            width: 28,
            height: 28,
        }),
        32: css({
            width: 32,
            height: 32,
        }),
        36: css({
            width: 36,
            height: 36,
        }),
    },
    spinning: css({
        animation: "spin 1s steps(8) infinite",
        "@keyframes spin": {
            from: {
                transform: "rotate(0deg)",
            },
            to: {
                transform: "rotate(360deg)",
            },
        },
    }),
};
